/* eslint-disable no-unused-vars */
import { Box, IconButton, Popover, Tooltip } from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CustomTypography from '../../../../components/mui/CustomTypography';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { truncateString } from '../../../../utils/TruncateStringUtils';
import { useDispatch, useSelector } from 'react-redux';
// import { editorVendorNoteRequest } from '../../Editor/actions';
import { videoExtensions } from '../../../../constants/SlectOptionConstants';
import CustomTooltip from '../../../../components/CustomTooltips';
import { updateSlateVendorNotes } from '../actions/slateDataActions';

import { getUserInfo } from '../../../../utils/UserInfoUtils';
import { useParams } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Forward5RoundedIcon from '@mui/icons-material/Forward5Rounded';
import Replay5RoundedIcon from '@mui/icons-material/Replay5Rounded';
import moment from 'moment';
import { NavLink } from 'react-bootstrap';
import Loading from './loading';
import useKeyPress from '../../../../hook/useKeyPress';
import SlowMotionVideoIcon from '@mui/icons-material/SlowMotionVideo';
import { Stack } from '@mui/material';

import translate_icon from '../../../../assets/images/translate-icon.svg';
import headphone_icon from '../../../../assets/images/headphone-icon.svg';
import rounded_icon from '../../../../assets/images/rounded-icon.svg';
import { glossaryPreviewRequest, resetGlossaryState } from '../actions/glossaryActions';
import { GLOSSARY_PREVIEW_SIZE } from '../../../../constants/GlossaryConstants';
import GlossarySection from './glossarySection';

const SidePanel = ({ isOpen, setIsOpen, sx }) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const userInfo = getUserInfo();
  const { id } = useParams();
  const { data: slateData } = useSelector((state) => state.slateData);
  const { rewindTime, syncWithAudio, autoScrollWithSync, audioStartTime } = useSelector((state) => state.settings);
  // const { lastWorkedParagraph } = useSelector((state) => state.settings);
  const { loading: editorDetailsLoading } = useSelector((state) => state.editorDetails);
  let editorDetails = useSelector((state) => state?.editorDetails?.user?.data?.value);
  let editorDetailsData = editorDetails && JSON.parse(editorDetails);
  // Client notes shown as bullets: default (template) notes first, then the client's own notes
  const clientNotes = [editorDetailsData?.DefaultClientNotes, editorDetailsData?.ClientNotes].filter(
    (note) => note && note.trim(),
  );
  const [VendorNotesData, setVendorNotesData] = useState(editorDetailsData?.VendorNote);
  const [isPlaying, setIsPlaying] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Deps are [id, dispatch] only — both stable, so the panel's frequent
  // re-renders (audio play/pause, settings) never re-fire this. Never add a
  // params object or a slice built in render: each is a new reference every
  // render and would turn this into a dispatch loop.
  useEffect(() => {
    if (!id) return;
    // Layout only resets when leaving /editor-copy entirely, so an in-place
    // :id change (next-transcript navigation) needs its own reset.
    dispatch(resetGlossaryState());
    dispatch(
      glossaryPreviewRequest({
        page: 1,
        rowNumber: GLOSSARY_PREVIEW_SIZE,
        sortRowName: 'CreatedOn',
        sortRowDirection: 'DESC',
        audilfileId: id,
        search: '',
      }),
    );
  }, [id, dispatch]);

  const timeoutRef = useRef(null);

  const handleRewind = () => {
    if (videoRef.current) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      videoRef.current.currentTime -= 5;
      timeoutRef.current = setTimeout(() => {
        videoRef.current.play().catch((err) => {
          console.error('Error trying to play:', err);
        });
      }, 200);
    }
  };

  const handleForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime += 5;
    }
  };

  useEffect(() => {
    const audioPlayer = videoRef?.current;
    if (!audioPlayer) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audioPlayer.addEventListener('play', handlePlay);
    audioPlayer.addEventListener('pause', handlePause);

    return () => {
      audioPlayer.removeEventListener('play', handlePlay);
      audioPlayer.removeEventListener('pause', handlePause);
    };
  }, [videoRef?.current]);

  // Note: Keeping this commented out since it was commented in the proofreader version
  // but the function definition is needed to match the vendor panel
  // const handleVendorNotes = (data) => {
  //   let params = {
  //     audioFileId: id,
  //     userId: userInfo?.UserId,
  //     note: data,
  //   };
  //   dispatch(editorVendorNoteRequest(params));
  // };

  let mediaFile = editorDetailsData?.UserFileName;
  let containsExtension = videoExtensions?.some((extension) =>
    mediaFile?.toLowerCase().includes(extension.toLowerCase()),
  );

  const handleTabToggle = () => {
    setIsPlaying((prevState) => !prevState);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // const handleResumeClick = useCallback(() => {
  //   const wordElement = document.querySelector(`#paragraph-${lastWorkedParagraph}`);

  //   if (wordElement) {
  //     wordElement.scrollIntoView({
  //       behavior: 'auto',
  //       block: 'center',
  //     });
  //   }
  // }, [lastWorkedParagraph]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePause = () => {
    if (videoRef.current && rewindTime) {
      videoRef.current.currentTime -= rewindTime;
    }
  };

  useKeyPress(handleForward, 'F8');
  useKeyPress(handleRewind, 'Tab', false, true);
  useKeyPress(handleTabToggle, 'Tab');

  const findCurrentParagraphAndSpan = useCallback((currentTime, transcript) => {
    let currentParagraph = -1;
    let currentSpan = -1;

    for (let i = 0; i < transcript.length; i++) {
      const paragraph = transcript[i];
      if (paragraph.type === 'paragraph' || paragraph.type === 'speaker') {
        for (let j = 0; j < paragraph.children.length; j++) {
          const word = paragraph.children[j];
          if ((word?.data?.start || 0) <= currentTime && (word?.data?.end || 0) >= currentTime) {
            currentParagraph = i;
            currentSpan = j;
            return { currentParagraph, currentSpan };
          }
        }
      }
    }

    return { currentParagraph, currentSpan };
  }, []);

  const clearHighlightStyles = useCallback(() => {
    const styleElement = document.querySelector('style[data-dynamic]');
    if (styleElement) {
      styleElement.textContent = '';
    }
  }, []);

  const handleTimeUpdate = useCallback(
    (time) => {
      const audioPlayer = videoRef.current;
      if (audioPlayer.paused) {
        clearHighlightStyles();
      } else {
        const { currentParagraph, currentSpan } = findCurrentParagraphAndSpan(time + 0.000001, slateData);

        requestAnimationFrame(() => {
          !audioPlayer.paused && updateHighlightStyles(currentParagraph, currentSpan);
          autoScrollWithSync && autoScrollToCurrentWord(currentParagraph, currentSpan);
        });
      }
    },
    [slateData, videoRef],
  );

  const autoScrollToCurrentWord = useCallback((currentParagraph, currentSpan) => {
    if (currentParagraph === -1 || currentSpan === -1) return;

    const wordElement = document.querySelector(`#paragraph-${currentParagraph} > span:nth-child(${currentSpan + 1})`);

    if (wordElement) {
      wordElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  const updateHighlightStyles = useCallback((currentParagraph, currentSpan) => {
    const styleElement = document.querySelector('style[data-dynamic]') || document.createElement('style');

    if (!styleElement.hasAttribute('data-dynamic')) {
      styleElement.setAttribute('data-dynamic', 'true');
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      #paragraph-${currentParagraph} > span:nth-child(${currentSpan + 1}) { 
        background: #d7d1fa !important; 
      }
    `;
  }, []);

  useEffect(() => {
    const audioPlayer = videoRef?.current;
    if (!audioPlayer) return;

    const onTimeUpdate = () => {
      syncWithAudio && handleTimeUpdate(audioPlayer.currentTime || 0);
    };

    audioPlayer.addEventListener('timeupdate', onTimeUpdate);
    return () => audioPlayer.removeEventListener('timeupdate', onTimeUpdate);
  }, [handleTimeUpdate]);

  useEffect(() => {
    if (audioStartTime) {
      const audioPlayer = videoRef?.current;
      if (audioPlayer) {
        audioPlayer.currentTime = audioStartTime;
      }
    }
  }, [audioStartTime]);

  useEffect(() => {
    if (isPlaying && videoRef && videoRef.current) {
      videoRef.current.play();
    } else if (videoRef && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isPlaying, rewindTime]);

  const getLanguageInitials = (language) => {
    const languageMap = {
      english: 'en',
      spanish: 'es',
      french: 'fr',
      japanese: 'ja',
      german: 'de',
      portuguese: 'pt',
      arabic: 'ar',
      korean: 'ko',
      chinese: 'cn',
      russian: 'ru',
      italian: 'it',
      none: 'en',
    };

    return (languageMap[language?.toLowerCase()] || language)?.toUpperCase();
  };
  const handleVendorNotes = (data) => {
    dispatch(updateSlateVendorNotes(data));
    // let params = {
    //   audioFileId: id,
    //   userId: userInfo?.UserId,
    //   note: data,
    // };
    // dispatch(editorVendorNoteRequest(params));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }} className="hide-scrollbar">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: 3,
          gap: '10px',
          borderBottom: `${isOpen ? '1px solid #ebebeb' : '0px solid #ebebeb'}`,
          paddingBottom: isOpen ? '10px' : '',
        }}
      >
        <MenuRoundedIcon onClick={() => setIsOpen(!isOpen)} sx={{ cursor: 'pointer' }} />{' '}
        <CustomTypography
          size="small"
          sx={{
            display: isOpen ? 'block' : 'none',
          }}
        >
          Collapses
        </CustomTypography>
      </Box>
      <Box
        in={!isOpen}
        sx={{
          marginLeft: 3,
          width: isOpen ? '302px' : '40px',
          // height: 'calc(100%-26px)',
        }}
      >
        <div id="transcriptSidebar">
          <Stack gap={1} sx={{ display: isOpen ? 'flex' : 'none' }}>
            <Stack direction={'row'} mt={editorDetailsData?.VerbatimTranscription || editorDetailsData?.PII ? 2 : 0}>
              {editorDetailsData?.VerbatimTranscription && (
                <CustomTooltip title="Every single utterance, noise and word spoken">
                  <div className="sub-btn align-content-center">Verbatim</div>
                </CustomTooltip>
              )}
              {editorDetailsData?.PII && (
                <CustomTooltip title={'Remove sensitive or private information'}>
                  <div className="sub-btn align-content-center">Redaction</div>
                </CustomTooltip>
              )}
            </Stack>
            <Stack direction={'row'}>
              {(editorDetailsData?.AudioLanguage || editorDetailsData?.ForeignLanguage) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mr: 2,
                  }}
                >
                  <div className="action-icon-btn">
                    <img src={translate_icon} className="img-fluid" alt="translate-icon" />
                  </div>
                  <CustomTypography size="small">
                    {getLanguageInitials(editorDetailsData?.AudioLanguage)}
                    {editorDetailsData?.AudioLanguage && editorDetailsData?.ForeignLanguage && ' - '}
                    {getLanguageInitials(editorDetailsData?.ForeignLanguage)}
                  </CustomTypography>
                </Box>
              )}
              {editorDetailsData && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <div className="action-icon-btn">
                    <img src={headphone_icon} className="img-fluid" alt="headphone-icon" />
                  </div>
                  <CustomTypography size="small">
                    {editorDetailsData?.Transcription ? 'Transcription' : 'Translation'}
                  </CustomTypography>
                </Box>
              )}
            </Stack>
          </Stack>

          <Box
            sx={{
              borderBottom: `${isOpen ? '1px solid #ebebeb' : '0px solid #ebebeb'}`,
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: '24px',
              letterSpacing: '0.01em',
              textAlign: 'left',
              padding: '10px 0',
              display: isOpen ? 'flex' : 'none',
              width: '346px',
              mt: 2,
            }}
          >
            <Box sx={{ marginRight: '0.5rem' }}>
              <img src={rounded_icon} style={{ maxWidth: '100%', height: 'auto' }} alt="rounded-icon" />{' '}
            </Box>
            <Box className="collapse-text" sx={{ display: isOpen ? 'block' : 'none' }}>
              Recordings
            </Box>
          </Box>
          <Box className="recodingWrapper" sx={{ display: isOpen ? 'block' : 'none' }}>
            <Box
              sx={{
                maxHeight: 'calc(100vh - 350px)',
                // minHeight: 'calc(100vh - 260px)',
                overflowX: 'hidden',
                // overflowY: 'auto',
                paddingRight: '5px',
                width: '346px',
              }}
              className="custom-scroll"
            >
              {editorDetailsLoading ? (
                <Loading sx={{ height: '161px' }} />
              ) : (
                editorDetailsData && (
                  <div className="recoding-video-player">
                    <div className="recoding-video-player-header">
                      <span className="video-label">
                        <Tooltip title={editorDetailsData?.UserFileName} placement="top">
                          {truncateString(editorDetailsData?.UserFileName, 22)}
                        </Tooltip>
                      </span>
                      {/* {lastWorkedParagraph != null && (
                        <IconButton as={NavLink} sx={{ padding: 0, fontSize: 0 }} onClick={handleResumeClick}>
                          <SlowMotionVideoIcon sx={{ fontSize: '16px' }} />
                        </IconButton>
                      )} */}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <div className="position-static">
                          <IconButton as={NavLink} sx={{ padding: 0, fontSize: 0 }} onClick={handleClick}>
                            <InfoOutlinedIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                          <Popover
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            container={document.body}
                            sx={{
                              '& .MuiPaper-root': {
                                width: '340px',
                                padding: '0px !important',
                                background: '#ffffff',
                                boxShadow: '0px 4px 4px 0px #00000040',
                                borderRadius: '20px !important',
                                border: 'none !important',
                                zIndex: 99,
                                top: '300px !important',
                                left: '328px !important',
                                overflowY: 'auto',
                              },
                            }}
                            disableScrollLock
                          >
                            <div className="file-info-header">
                              <CustomTypography size="mediumSmall" weight="bold">
                                File Information
                              </CustomTypography>
                            </div>
                            <ul className="file-list-ul">
                              {editorDetailsData?.ProjectName && (
                                <li>
                                  Project Folder{' '}
                                  <span className="file-list-content">: {editorDetailsData?.ProjectName}</span>
                                </li>
                              )}
                              {editorDetailsData?.OrderId && (
                                <li>
                                  Order No <span className="file-list-content">: {editorDetailsData?.OrderId}</span>
                                </li>
                              )}
                              {(editorDetailsData?.VerbatimTranscription ||
                                editorDetailsData?.PII ||
                                editorDetailsData?.Timestamps) && (
                                <li>
                                  Options{' '}
                                  <span className="file-list-content">
                                    :{' '}
                                    {[
                                      editorDetailsData?.VerbatimTranscription && 'Verbatim',
                                      editorDetailsData?.PII && 'Redact',
                                      editorDetailsData?.Timestamps && 'Timestamps',
                                    ]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </span>
                                </li>
                              )}
                              {editorDetailsData?.turnAroundValue && (
                                <li>
                                  TAT <span className="file-list-content">: {editorDetailsData?.turnAroundValue}</span>
                                </li>
                              )}
                              {editorDetailsData?.TranscriptionType && (
                                <li>
                                  Type{' '}
                                  <span className="file-list-content">: {editorDetailsData?.TranscriptionType}</span>
                                </li>
                              )}
                              {editorDetailsData?.OrderDate && (
                                <li>
                                  Order Date{' '}
                                  <span className="file-list-content">
                                    : {moment(editorDetailsData?.OrderDate).format('DD-MM-YYYY')}
                                  </span>
                                </li>
                              )}
                              {editorDetailsData?.ForeignLanguage && (
                                <li>
                                  Transcript Lang{' '}
                                  <span className="file-list-content">: {editorDetailsData?.ForeignLanguage}</span>
                                </li>
                              )}
                              {editorDetailsData?.DueDate && (
                                <li>
                                  Vendor Due{' '}
                                  <span className="file-list-content">
                                    :{' '}
                                    {editorDetailsData?.DueDate !== null
                                      ? moment(editorDetailsData?.DueDate).format('MM/DD/YYYY')
                                      : '--'}
                                  </span>
                                </li>
                              )}
                              {editorDetailsData?.VendorName && (
                                <li>
                                  Original Vendor{' '}
                                  <span className="file-list-content">: {editorDetailsData?.VendorName}</span>
                                </li>
                              )}
                            </ul>
                          </Popover>
                        </div>
                      </Box>
                    </div>
                    {editorDetailsData?.MediaFile && (
                      <>
                        {containsExtension === true ? (
                          <video
                            id={`audiofile_${editorDetailsData?.Id}`}
                            ref={videoRef}
                            src={editorDetailsData?.MediaFile}
                            controls
                            preload={'metadata'}
                            autoPlay={isPlaying}
                            onPause={handlePause}
                            style={{
                              width: '100%',
                              padding: '0px 15px 0px 15px',
                              height: '170px',
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            controlsList="nodownload " // Disable download
                            disablePictureInPicture
                          />
                        ) : (
                          <audio
                            id={`audiofile_${editorDetailsData?.Id}`}
                            ref={videoRef}
                            src={editorDetailsData?.MediaFile}
                            preload={'metadata'}
                            autoPlay={isPlaying}
                            onPause={handlePause}
                            controls={true}
                            style={{
                              width: '100%',
                              padding: '0px 15px 0px 15px',
                            }}
                            onContextMenu={(e) => e.preventDefault()}
                            controlsList={'nodownload '} // Disable download
                          />
                        )}
                      </>
                    )}
                    <div className="preview-recoding-duration">
                      {editorDetailsData?.Duration && (
                        <div>
                          <TimerOutlinedIcon sx={{ fontSize: '20px' }} /> {editorDetailsData?.Duration}
                        </div>
                      )}
                      {editorDetailsData?.MediaFile && (
                        <div id="playerButtons">
                          <Tooltip title="Rewind" placement="top">
                            <IconButton
                              sx={{
                                mx: 1,
                                p: 0,
                                border: 'none',
                                color: '#83878a',
                                background: 'transparent',
                              }}
                              id="btnRewind"
                              onClick={handleRewind}
                            >
                              <Replay5RoundedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Forward" placement="top">
                            <IconButton
                              sx={{
                                mx: 1,
                                p: 0,
                                border: 'none',
                                color: '#83878a',
                                background: 'transparent',
                              }}
                              id="btnForward"
                              onClick={handleForward}
                            >
                              <Forward5RoundedIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}
              <div className="notesContent">
                <div className="notesTitle">
                  Client Notes {''}
                  <Tooltip
                    title="These are the client's instructions, kindly ensure they are adhered to while processing the file."
                    placement="right"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '14px' }} />
                  </Tooltip>
                </div>
                {clientNotes.length > 0 ? (
                  <ul className="clientNotesList">
                    {clientNotes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                ) : (
                  <div>-</div>
                )}
              </div>
              <div className="notesContent">
                <div className="notesTitle">
                  Vendor Notes{''}
                  <Tooltip
                    title="These are the notes provided by the vendor for your reference. Please ensure alignment with the project requirements."
                    placement="right"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: '14px' }} />
                  </Tooltip>
                </div>
                <textarea
                  rows="2"
                  className="notesItem"
                  disabled
                  style={{
                    height: '100%',
                    width: '100%',
                  }}
                  value={(VendorNotesData !== undefined ? VendorNotesData : editorDetailsData?.VendorNote) || ''}
                  onChange={(e) => setVendorNotesData(e.target.value)}
                  onBlur={(e) => handleVendorNotes(e.target.value)}
                />
              </div>
              <GlossarySection />
            </Box>
          </Box>
        </div>
      </Box>
    </Box>
  );
};

export default SidePanel;
